import type { Sanction } from "$lib/types";
import { getEngine } from "$lib/engine-instance";

/** Normalize a string for accent-insensitive search ("Pawel" finds "Pawe\u0142"). */
export function normalizeSearch(s: string): string {
  return getEngine().foldAscii(s).toLowerCase();
}

/** Splits on any run of non-alphanumerics, applied to BOTH the indexed field and the query, so every
 * search is word-prefix ("vin" finds Vincent, "inc" finds nobody) \u2014 matching an email as one substring would re-admit mid-name hits. */
export function searchTokens(s: string): string[] {
  return normalizeSearch(s).split(/[^\p{L}\p{N}]+/u).filter(Boolean);
}

/** Every query term must word-prefix one of `tokens`. Empty terms match all. */
export function matchesAllTerms(tokens: string[], terms: string[]): boolean {
  return terms.every((term) => tokens.some((tok) => tok.startsWith(term)));
}

export function formatScore(gw: number, vp: number, tp?: number): string {
  const s = gw > 0 ? `${gw}GW${vp}` : `${vp}VP`;
  return tp === undefined ? s : `${s} ${tp}TP`;
}

export function formatGwTp(gw: number, tp: number): string {
  return gw > 0 ? `${gw}GW ${tp}TP` : `${tp}TP`;
}

/** Offset (ms) of an IANA timezone at a given instant. */
function tzOffsetMs(timeZone: string, date: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(date);
  const get = (type: string) => +(parts.find((p) => p.type === type)?.value ?? "0");
  const hour = get("hour");
  const asUtc = Date.UTC(
    get("year"), get("month") - 1, get("day"),
    hour === 24 ? 0 : hour, get("minute"), get("second"),
  );
  return asUtc - date.getTime();
}

/** Interprets a naive ISO datetime (no offset) as wall-clock time in the given IANA timezone —
 * Tournament.start/finish are stored this way, so plain `new Date(iso)` would misread them as browser-local. Strings with an offset parse as-is. */
export function zonedDate(iso: string, timeZone: string): Date {
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(iso)) return new Date(iso);
  const asUtc = new Date(iso + "Z");
  if (isNaN(asUtc.getTime())) return new Date(iso);
  // two passes: the offset near the target instant, corrected for DST edges
  const approx = asUtc.getTime() - tzOffsetMs(timeZone, asUtc);
  return new Date(asUtc.getTime() - tzOffsetMs(timeZone, new Date(approx)));
}

/** The viewer's local calendar date as `YYYY-MM-DD`, comparable as text against League.start/finish. */
export function localToday(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** A `YYYY-MM-DD` calendar date: `new Date` reads it as UTC midnight, so it is formatted in UTC to keep its day. */
export function formatCalendarDate(d: string): string {
  return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
}

/** Sanctions visible on member-directory surfaces. Cautions stay private to their tournament (even
 * IC/Ethics see them only inside it); warnings/SA/DQ are member-visible for 18 months, suspension/probation are membership-level. */
export function visibleSanctions(sanctions: Sanction[]): Sanction[] {
  return sanctions.filter(s => s.level !== "caution");
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
